import { RenewalSummary } from '@policyvision/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DrawerHeader } from '@/common/components/drawer/DrawerHeader';
import { Sheet, SheetContent, SheetTitle } from '@/common/components/ui/sheet';
import { Skeleton } from '@/common/components/ui/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/common/components/ui/tabs';
import { getEpicPolicyURL } from '@/policy/utils/epicUrl';

import { useFetchRenewalById } from '../hooks/useFetchRenewalById';
import RenewalAnalysisTab from './drawer/RenewalAnalysisTab';
import RenewalNotesTab from './drawer/RenewalNotesTab';

interface IRenewalDrawerProps {
  renewal: RenewalSummary | null;
  isOpen: boolean;
  onClose: () => void;
  handleDrawerSwitch: () => void;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

const RenewalDrawer = ({
  renewal,
  isOpen,
  onClose,
  handleDrawerSwitch,
  onNavigateNext,
  onNavigatePrevious,
  hasPrevious,
  hasNext,
}: IRenewalDrawerProps) => {
  const { t } = useTranslation();
  const { renewalDetails, isLoading } = useFetchRenewalById(renewal?.uuid);
  const [activeTab, setActiveTab] = useState<string>('analyse');
  const closeDrawer = () => {
    onClose();
  };
  return (
    <Sheet open={isOpen} modal={false}>
      <SheetContent
        className="w-full sm:max-w-2xl p-0 top-13.75 h-[calc(100vh-55px)]"
        hideDefaultX
        hideOverlay
      >
        {renewal == null ? (
          <>
            <SheetTitle className="sr-only">
              {t('renewal.renewalDrawer')}
            </SheetTitle>
            <Skeleton className="h-full" />
          </>
        ) : (
          <div className="flex flex-col h-full">
            <DrawerHeader
              title={renewal.enterpriseName}
              externalLink={{
                label: renewal.policyNumber,
                path: getEpicPolicyURL(renewal.policyGuid) ?? '',
              }}
              onClose={closeDrawer}
              onNavigatePrevious={onNavigatePrevious}
              onNavigateNext={onNavigateNext}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
            />

            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex flex-col flex-1 overflow-hidden gap-0"
            >
              <TabsList className="w-full rounded-none pb-0 bg-primary-foreground h-auto shrink-0">
                <TabsTrigger
                  value="analyse"
                  className="cursor-pointer rounded-none bg-primary-foreground text-foreground dark:text-foreground py-2 border-0 border-b-2 border-transparent data-[state=active]:bg-primary-foreground! data-[state=active]:text-foreground data-[state=active]:border-b-ring! data-[state=active]:shadow-none"
                >
                  {t('renewal.drawer.analyseTab')}
                </TabsTrigger>
                <TabsTrigger
                  value="notes"
                  className="cursor-pointer rounded-none bg-primary-foreground text-foreground dark:text-foreground py-2 border-0 border-b-2 border-transparent data-[state=active]:bg-primary-foreground! data-[state=active]:text-foreground data-[state=active]:border-b-ring! data-[state=active]:shadow-none"
                >
                  {t('renewal.drawer.notesTab')}
                </TabsTrigger>
              </TabsList>
              <div className="flex-1 overflow-y-auto">
                <TabsContent value="analyse" className="flex-1 flex flex-col">
                  {!renewalDetails || isLoading ? (
                    <span>{t('renewal.drawer.loadingData')}</span>
                  ) : (
                    <RenewalAnalysisTab
                      renewalDetails={renewalDetails}
                      handleDrawerSwitch={handleDrawerSwitch}
                    />
                  )}
                </TabsContent>

                <TabsContent value="notes" className="flex-1 flex flex-col m-0">
                  <RenewalNotesTab
                    renewalDetails={renewalDetails ?? null}
                    isLoading={isLoading}
                  />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default RenewalDrawer;
